import React, {useEffect, useState} from 'react';
import {AlertTriangle} from 'lucide-react';
import {cva} from 'class-variance-authority';
import {cn} from '@matic/lib/utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/matic/components/ui/dialog';
import {Button} from '@/matic/components/ui/button';
import {Input} from '@/matic/components/ui/input';

const iconVariants = cva('h-6 w-6', {
  variants: {
    variant: {
      destructive: 'text-red-600',
      primary: 'text-yellow-600',
      warning: 'text-orange-600',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

type ConfirmationModalProps = {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'destructive' | 'primary' | 'warning';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  hideConfirm?: boolean;
  /**
   * When provided, renders a text input below the description and keeps
   * the Confirm button disabled until the user types this string
   * exactly. Used to gate irreversible actions (e.g. group hard-delete)
   * behind a deliberate typed confirmation.
   */
  requireTypedConfirmation?: string;
  /**
   * Optional extra content rendered above the footer buttons — used to
   * surface structured error details (e.g. the 409 `group_has_references`
   * breakdown) inside the confirm modal without re-opening a toast.
   */
  extraContent?: React.ReactNode;
};

export function ConfirmationModal({
  open,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  onConfirm,
  onCancel,
  isLoading = false,
  hideConfirm = false,
  requireTypedConfirmation,
  extraContent,
}: ConfirmationModalProps) {
  const [typedValue, setTypedValue] = useState('');

  useEffect(() => {
    if (!open) setTypedValue('');
  }, [open]);

  const typedConfirmationMatches =
    requireTypedConfirmation === undefined ||
    typedValue === requireTypedConfirmation;
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertTriangle className={cn(iconVariants({variant}))} />
            </div>
            <div>
              <DialogTitle className="text-lg font-medium text-gray-900">
                {title}
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="mt-3 text-sm text-gray-500">
            {description}
          </DialogDescription>
        </DialogHeader>

        {requireTypedConfirmation !== undefined && (
          <div className="space-y-2">
            <label
              htmlFor="confirmation-modal-typed-input"
              className="text-sm font-medium text-gray-700"
            >
              Type <span className="font-mono">{requireTypedConfirmation}</span>{' '}
              to confirm
            </label>
            <Input
              id="confirmation-modal-typed-input"
              value={typedValue}
              disabled={isLoading}
              autoComplete="off"
              onChange={(event) => {
                setTypedValue(event.target.value);
              }}
            />
          </div>
        )}

        {extraContent}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          {!hideConfirm && (
            <Button
              type="button"
              variant={variant}
              onClick={onConfirm}
              disabled={isLoading || !typedConfirmationMatches}
            >
              {confirmText}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
